import type { DocumentDefinition, Workspace } from "./types";

const now = () => new Date().toISOString();

export function createWorkspace(): Workspace {
  return {
    version: 1,
    answers: {
      trustName: "The Morgan Family Living Trust",
      settlorName: "Jordan Morgan",
      coSettlorName: "",
      county: "Clark",
      residenceState: "Nevada",
      successorTrustee: "Avery Morgan",
      backupTrustee: "Rowan Morgan",
      primaryBeneficiary: "Avery Morgan",
      distributionPlan: "Distribute the remaining trust estate in equal shares to the settlor's descendants, by right of representation.",
      minorBeneficiaries: true,
      blendedFamily: false,
      specialNeedsBeneficiary: false,
      businessOwnership: false,
      outOfStateRealEstate: false,
      nonUsPerson: false,
      creditorProtectionGoal: false,
    },
    people: [
      { id: "settlor", name: "Jordan Morgan", role: "Settlor and initial trustee", detail: "Creates and manages the revocable trust during life." },
      { id: "successor", name: "Avery Morgan", role: "First successor trustee", detail: "Steps in after incapacity, resignation, or death." },
      { id: "backup", name: "Rowan Morgan", role: "Backup successor trustee", detail: "Serves if the first successor cannot act." },
      { id: "beneficiary", name: "Avery Morgan", role: "Primary beneficiary", detail: "Receives the trust estate under the distribution plan." },
    ],
    assets: [
      { id: "home", name: "Primary residence", kind: "Real estate", value: 680000, status: "Action needed" },
      { id: "checking", name: "Household checking", kind: "Banking", value: 28500, status: "Funded" },
      { id: "brokerage", name: "Taxable investment account", kind: "Brokerage", value: 245000, status: "Action needed" },
      { id: "personal", name: "Personal property assignment", kind: "Personal property", value: 36000, status: "Funded" },
      { id: "retirement", name: "Retirement beneficiary review", kind: "Brokerage", value: 310000, status: "Review beneficiary" },
    ],
    execution: [
      { id: "review", title: "Review generated documents", detail: "Confirm names, roles, distribution terms, and property details.", done: false },
      { id: "attorney", title: "Resolve flagged review items", detail: "Complete attorney review when complexity flags are present.", done: false },
      { id: "sign", title: "Sign trust and supporting documents", detail: "Follow the ceremony notes for signatures, witnesses, and notarization.", done: false },
      { id: "deed", title: "Record real estate deed", detail: "Prepare and record any required deed with the correct county recorder.", done: false },
      { id: "fund", title: "Complete asset funding", detail: "Retitle eligible assets and confirm beneficiary designations.", done: false },
      { id: "store", title: "Store final package", detail: "Keep originals securely and share access instructions with the successor trustee.", done: false },
    ],
    reviews: [],
    lastUpdated: now(),
  };
}

export const documentDefinitions: DocumentDefinition[] = [
  { id: "trust", title: "Revocable Living Trust", category: "Core trust", description: "Nevada-governed trust agreement with lifetime administration and successor-trustee provisions.", readiness: "complete" },
  { id: "certificate", title: "Certification of Trust", category: "Core trust", description: "Short-form trust confirmation for banks and other third parties.", readiness: "complete" },
  { id: "property", title: "Assignment of Personal Property", category: "Funding", description: "General assignment for eligible tangible personal property.", readiness: "complete" },
  { id: "schedule", title: "Schedule of Trust Property", category: "Funding", description: "Working list of assets intended to be held by or coordinated with the trust.", readiness: "in-progress" },
  { id: "will", title: "Pour-over Will", category: "Supporting plan", description: "Draft companion will for assets remaining outside the trust.", readiness: "review" },
  { id: "financial-poa", title: "Durable Financial Power of Attorney", category: "Supporting plan", description: "Draft financial authority document for incapacity planning.", readiness: "review" },
  { id: "healthcare", title: "Health-Care Directive Checklist", category: "Supporting plan", description: "Execution checklist for Nevada health-care planning documents.", readiness: "review" },
  { id: "funding", title: "Funding Action Plan", category: "Funding", description: "Asset-by-asset transfer and beneficiary-designation checklist.", readiness: "in-progress" },
];
